import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { urls } from './config.dev';
import './contacts.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit, faTrash } from '@fortawesome/free-solid-svg-icons';
import { faComment, faWallet, faCheckCircle, faTimesCircle } from '@fortawesome/free-solid-svg-icons';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import LoadingState from './LoadingState';
import Topbar from './Topbar';
import AddToWalletPrompt from './AddToWalletPrompt';

function Contacts() {
  const [contacts, setContacts] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [newContactVisible, setNewContactVisible] = useState(false);
  const [sendMessageVisible, setSendMessageVisible] = useState(false);
  const [newContact, setNewContact] = useState({ first_name: '', last_name: '', phone_number: '', age: '', sex: '', dob: '', religion: '', address: '' });
  const [message, setMessage] = useState('');
  const [filteredContacts, setFilteredContacts] = useState([]);
  const [showSendMessagePrompt, setShowSendMessagePrompt] = useState(false);
  const [showAddContactPrompt, setShowAddContactPrompt] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [showSendMessageToAllPrompt, setShowSendMessageToAllPrompt] = useState(false);
  const [messageToAllPatients, setMessageToAllPatients] = useState('');
  const [messageToAllPatientsMuslims, setMessageToAllPatientsMuslims] = useState('');
  const [messageToAllPatientsChristians, setMessageToAllPatientsChristians] = useState('');
  const [employeeName, setEmployeeName] = useState('');
  const [isAddingContact, setIsAddingContact] = useState(false);
  const [editContactVisible, setEditContactVisible] = useState(false);
  const [editedContact, setEditedContact] = useState({});
  const [clinicSessionToken, setClinicSessionToken] = useState('');
  const [token, setToken] = useState('');
  const navigate = useNavigate();
  const [canSendReminder, setCanSendReminder] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const params = new URLSearchParams(window.location.search);
  const urlToken = params.get('token');
  const [isWalletPromptOpen, setIsWalletPromptOpen] = useState(false);
  const [selectedContactId, setSelectedContactId] = useState(null);
  const [showCategorySelectionPrompt, setShowCategorySelectionPrompt] = useState(false);
  const [selectedReligion, setSelectedReligion] = useState('');
  const [selectedCategories, setSelectedCategories] = useState(['active']);
  const [filteredContactsForMessaging, setFilteredContactsForMessaging] = useState([]);
  const [showRecipientList, setShowRecipientList] = useState(false);
  const [validNumbersCount, setValidNumbersCount] = useState(0);
  const [invalidNumbersCount, setInvalidNumbersCount] = useState(0);

  // Function to validate phone number format
  const isValidPhoneNumber = (number) => {
    if (!number) return false;
    // Check for 12 digits starting with 256
    if (/^256\d{9}$/.test(number)) return true;
    // Check for 10 digits starting with 0
    if (/^0\d{9}$/.test(number)) return true;
    // Check for 12 digits starting with +256
    if (/^\+256\d{9}$/.test(number)) return true;
    return false;
  };

  // Function to format phone number consistently
  const formatPhoneNumber = (number) => {
    if (!number) return '';
    // Remove any non-digit characters
    const cleaned = number.replace(/\D/g, '');
    // Convert to 256 format if it starts with 0
    if (cleaned.startsWith('0') && cleaned.length === 10) {
      return '256' + cleaned.substring(1);
    }
    // Ensure it starts with 256
    if (cleaned.startsWith('256') && cleaned.length === 12) {
      return cleaned;
    }
    return number; // Return original if we can't format it
  };

  useEffect(() => {
    const fetchTokenAndCheckSecurity = async () => {
      try {
        const params = new URLSearchParams(window.location.search);
        const tokenFromUrl = params.get('token');

        console.log('URL Token:', tokenFromUrl);

        const securityResponse = await fetch(urls.security, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ token: tokenFromUrl }),
        });

        if (securityResponse.ok) {
          const securityData = await securityResponse.json();

          if (securityData.message === 'Session valid') {
            setEmployeeName(securityData.employee_name);
            console.log('Clinic Session Token:', securityData.clinic_session_token);
            setClinicSessionToken(securityData.clinic_session_token);

            if (securityData.clinic_session_token) {
              setToken(securityData.clinic_session_token);
              fetchContacts(securityData.clinic_session_token);
              fetchMessagingPermission();
            }
          } else if (securityData.error === 'Session expired') {
            navigate(`/dashboard?token=${securityData.clinic_session_token}`);
          } else {
            navigate('/login');
          }
        } else {
          throw new Error('Failed to perform security check');
        }
      } catch (error) {
        console.error('Error performing security check:', error);
        navigate('/login');
      }
    };

    fetchTokenAndCheckSecurity();
  }, [navigate]);

  const fetchContacts = async (token) => {
    try {
      const params = new URLSearchParams(window.location.search);
      const tokenFromUrl = params.get('token');

      const response = await fetch(urls.fetchcontacts, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ token: tokenFromUrl }),
      });

      if (response.ok) {
        const data = await response.json();
        const sortedContacts = data.sort((a, b) => a.first_name.localeCompare(b.first_name));
        setContacts(sortedContacts);
        setFilteredContacts(sortedContacts);
      } else {
        console.error('Failed to fetch contacts:', response.statusText);
      }
    } catch (error) {
      console.error('Error fetching contacts:', error);
    }
  };

  const fetchFilteredContacts = async (religion, categories) => {
    try {
      const params = new URLSearchParams(window.location.search);
      const tokenFromUrl = params.get('token');

      const response = await fetch(urls.fetchcontacts2, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          token: tokenFromUrl,
          religion: religion,
          categories: categories
        }),
      });

      if (response.ok) {
        const data = await response.json();
        // Analyze phone numbers
        let validCount = 0;
        let invalidCount = 0;
        
        data.forEach(contact => {
          if (isValidPhoneNumber(contact.phone_number)) {
            validCount++;
          } else {
            invalidCount++;
          }
        });
        
        setValidNumbersCount(validCount);
        setInvalidNumbersCount(invalidCount);
        
        return data;
      } else {
        console.error('Failed to fetch filtered contacts:', response.statusText);
        return [];
      }
    } catch (error) {
      console.error('Error fetching filtered contacts:', error);
      return [];
    }
  };

  const fetchMessagingPermission = async () => {
    try {
      const params = new URLSearchParams(window.location.search);
      const tokenFromUrl = params.get('token');

      if (!tokenFromUrl) {
        throw new Error('Token not found in URL parameters');
      }

      const response = await fetch(urls.messagingPermission, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ token: tokenFromUrl }),
      });

      if (response.ok) {
        const data = await response.json();
        if (data.messages === 'yes') {
          setCanSendReminder(true);
        } else {
          setCanSendReminder(false);
        }
      } else {
        throw new Error('Failed to fetch messaging permission');
      }
    } catch (error) {
      console.error('Error fetching messaging permission:', error.message);
    }
  };

  const editContact = (contact) => {
    setEditedContact(contact);
    setEditContactVisible(true);
  };
  
  const updateContact = async () => {
    setIsAddingContact(true);
  
    try {
      const params = new URLSearchParams(window.location.search);
      const tokenFromUrl = params.get('token');
  
      const response = await fetch(urls.editcontact, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...editedContact,
          contact_id: editedContact.contact_id,
          token: tokenFromUrl,
        }),
      });
  
      const responseData = await response.json();
  
      if (response.ok) {
        setEditContactVisible(false);
        fetchContacts(tokenFromUrl);
        alert(responseData.message);
      } else {
        console.error('Failed to edit contact:', response.statusText);
      }
    } catch (error) {
      console.error('Error editing contact:', error);
    }
  
    setIsAddingContact(false);
  };
  
  const addContact = async () => {
    if (!newContact.first_name || !newContact.last_name || !newContact.phone_number || !newContact.age || !newContact.sex || !newContact.dob || !newContact.religion) {
      alert('Please fill in all fields');
      return;
    }
  
    setIsAddingContact(true);
  
    try {
      const params = new URLSearchParams(window.location.search);
      const tokenFromUrl = params.get('token');
  
      const response = await fetch(urls.addcontact, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...newContact,
          token: tokenFromUrl
        }),
      });
  
      const responseData = await response.json();
  
      if (response.ok) {
        setShowAddContactPrompt(false);
        fetchContacts(tokenFromUrl);
        alert(responseData.message);
      } else {
        console.error('Failed to add contact:', response.statusText);
      }
    } catch (error) {
      console.error('Error adding contact:', error);
    }
  
    setIsAddingContact(false);
  };
  
  const removeContact = async (contactId, phoneNumber) => {
    if (window.confirm('Are you sure you want to remove this contact?')) {
      try {
        const params = new URLSearchParams(window.location.search);
        const tokenFromUrl = params.get('token');
  
        const response = await fetch(urls.removecontact, {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            contactId,
            phoneNumber,
            token: tokenFromUrl
          }),
        });
  
        if (response.ok) {
          const updatedContacts = contacts.filter(contact => contact.contact_id !== contactId);
          setContacts(updatedContacts);
          setFilteredContacts(updatedContacts);
          alert('Contact removed successfully');
        } else {
          console.error('Failed to remove contact:', response.statusText);
        }
      } catch (error) {
        console.error('Error removing contact:', error);
      }
    }
  };
  
  const sendMessage = async (phoneNumber) => {
    setShowSendMessagePrompt(true);
    setPhoneNumber(phoneNumber);
  };

  const sendActualMessage = async (phoneNumber) => {
    setIsLoading(true);
  
    try {
      const params = new URLSearchParams(window.location.search);
      const tokenFromUrl = params.get("token");
  
      const messageLength = message.length;
      let costPerSms = messageLength * 0.9;
  
      if (costPerSms < 150) {
        costPerSms = 150;
      }
  
      const totalCost = costPerSms;
  
      const confirmationMessage = `
        📢 **Message Sending Summary**
        ------------------------------------
        💬 Message Length: **${messageLength} characters**
        💰 Estimated Cost: **UGX ${totalCost.toLocaleString()}**
  
        🚫 Note: Ensure your wallet balance is sufficient.
        ➡️ Press **OK** to continue sending message.
      `;
  
      if (!window.confirm(confirmationMessage)) {
        setIsLoading(false);
        setShowSendMessageToAllPrompt(false);
        return;
      }
  
      const response = await fetch(urls.whatsappall2, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          phoneNumber: phoneNumber,
          message: message,
          estimatedCost: totalCost,
          token: tokenFromUrl,
        }),
      });
  
      const data = await response.json();
  
      if (data.message === "SMS sent successfully") {
        toast.success("📩 SMS sent successfully!", {
          position: "top-right",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
        });
      } else {
        toast.error("❌ Failed to send SMS. Try again.", {
          position: "top-right",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
        });
      }
    } catch (error) {
      toast.error("❌ An error occurred. Please try again.", {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });
    } finally {
      setIsLoading(false);
    }
  };
  
  const sendMessageToAllPatients = (religion) => {
    setSelectedReligion(religion);
    setShowCategorySelectionPrompt(true);
  };

  const handleCategorySelection = async () => {
    if (selectedCategories.length === 0) {
      alert('Please select at least one category');
      return;
    }

    setShowCategorySelectionPrompt(false);
    
    const fetchedContacts = await fetchFilteredContacts(selectedReligion, selectedCategories);
    setFilteredContactsForMessaging(fetchedContacts);
    
    setNewContact({ ...newContact, religion: selectedReligion });
    setShowRecipientList(true); // Show recipient list first
  };

  const handleCategoryChange = (category) => {
    setSelectedCategories(prev => {
      if (prev.includes(category)) {
        return prev.filter(cat => cat !== category);
      } else {
        return [...prev, category];
      }
    });
  };
  
  const proceedToMessageComposition = () => {
    setShowRecipientList(false);
    setShowSendMessageToAllPrompt(true);
  };
  
  const sendToAllPatients = async () => {
    setIsLoading(true);
  
    try {
      const params = new URLSearchParams(window.location.search);
      const tokenFromUrl = params.get("token");
  
      const allNumbers = filteredContactsForMessaging.map(contact => contact.phone_number);
  
      const validNumbersSet = new Set();
      const invalidNumbers = [];
  
      allNumbers.forEach(number => {
        if (/^07\d{8}$/.test(number) || /^2567\d{8}$/.test(number)) {
          validNumbersSet.add(number);
        } else {
          invalidNumbers.push(number);
        }
      });
  
      const validNumbers = Array.from(validNumbersSet);
      const invalidCount = invalidNumbers.length;
      const validCount = validNumbers.length;
  
      const messageContent = selectedReligion === "Islamic" ? messageToAllPatientsMuslims : messageToAllPatientsChristians;
      const messageLength = messageContent.length;
      let costPerSms = messageLength * 0.9;
  
      if (costPerSms < 150) {
        costPerSms = 150;
      }
  
      const totalCost = costPerSms * validCount;
  
      let maxCost = null;
      const wantsToSetLimit = window.confirm("Do you want to set a maximum cost for this action?");
      
      if (wantsToSetLimit) {
        let inputAmount = prompt("Enter the maximum amount you want to spend (UGX):", "");
        if (inputAmount && /^\d+$/.test(inputAmount)) {
          maxCost = parseInt(inputAmount, 10);
        }
      }

      const categoryText = selectedCategories.join(', ');
  
      const confirmationMessage = `
        📢 **Message Sending Summary**
        ------------------------------------
        🏷️ Religion: **${selectedReligion}**
        📂 Categories: **${categoryText}**
        👥 Total Contacts: **${filteredContactsForMessaging.length}**
        ❌ ${invalidCount} phone numbers are invalid.
        ✅ ${validCount} valid phone numbers found.
        📝 Message Length: **${messageLength} characters**
        💰 Estimated Cost: **UGX ${totalCost.toLocaleString()}**
        ${maxCost ? `🔹 Maximum Spending Limit: **UGX ${maxCost.toLocaleString()}**` : ""}
        
        🚫 Note: Inactive patients will **not** receive the SMS, reducing the final cost.
        ⚠️ Ensure your wallet balance is sufficient.
        ➡️ Press **OK** to continue sending messages.
      `;
  
      if (!window.confirm(confirmationMessage)) {
        setIsLoading(false);
        return;
      }
  
      const payload = {
        phoneNumbers: validNumbers,
        message: messageContent,
        token: tokenFromUrl,
      };
      
      if (maxCost !== null) {
        payload.maxCost = maxCost;
      }
  
      const response = await fetch(urls.whatsappall, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });
  
      if (response.ok) {
        const responseData = await response.json();
        displayNotification(responseData);
      } else {
        toast.error(`❌ Failed to send messages: ${response.statusText}`, {
          position: "top-right",
          autoClose: 10000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
        });
      }
    } catch (error) {
      toast.error(`❌ Error: ${error.message}`, {
        position: "top-right",
        autoClose: 10000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });
    }
  
    setIsLoading(false);
    setShowSendMessageToAllPrompt(false);
  };

  const displayNotification = (data) => {
    const { success_count, current_balance, sent_names, total_deduction_cost } = data;
    const cost = total_deduction_cost;
    
    // Create a modal container
    const modalContainer = document.createElement('div');
    modalContainer.style.position = 'fixed';
    modalContainer.style.top = '0';
    modalContainer.style.left = '0';
    modalContainer.style.width = '100%';
    modalContainer.style.height = '100%';
    modalContainer.style.backgroundColor = 'rgba(0, 0, 0, 0.7)';
    modalContainer.style.display = 'flex';
    modalContainer.style.justifyContent = 'center';
    modalContainer.style.alignItems = 'center';
    modalContainer.style.zIndex = '1000';
    
    // Create modal content
    const modalContent = document.createElement('div');
    modalContent.style.backgroundColor = 'white';
    modalContent.style.borderRadius = '8px';
    modalContent.style.width = '600px';
    modalContent.style.maxWidth = '90%';
    modalContent.style.maxHeight = '80vh';
    modalContent.style.display = 'flex';
    modalContent.style.flexDirection = 'column';
    modalContent.style.boxShadow = '0 4px 20px rgba(0, 0, 0, 0.15)';
    
    // Create header with close button
    const header = document.createElement('div');
    header.style.display = 'flex';
    header.style.justifyContent = 'flex-end';
    header.style.padding = '15px 15px 0 0';
    
    const closeButton = document.createElement('span');
    closeButton.innerHTML = '&times;';
    closeButton.style.fontSize = '24px';
    closeButton.style.cursor = 'pointer';
    closeButton.style.color = '#333';
    closeButton.onclick = () => {
        document.body.removeChild(modalContainer);
    };
    header.appendChild(closeButton);
    
    // Create main content
    const content = document.createElement('div');
    content.style.padding = '20px';
    content.style.overflowY = 'auto';
    
    // Summary section
    const summary = document.createElement('div');
    summary.style.marginBottom = '20px';
    summary.style.borderBottom = '1px solid #eee';
    summary.style.paddingBottom = '20px';
    
    const successText = document.createElement('p');
    successText.textContent = `${success_count} messages sent successfully`;
    successText.style.fontSize = '18px';
    successText.style.marginBottom = '10px';
    successText.style.color = '#333';
    
    const costText = document.createElement('p');
    costText.innerHTML = `<strong>Cost:</strong> UGX ${cost.toLocaleString()}`;
    costText.style.marginBottom = '8px';
    costText.style.color = '#555';
    
    const balanceText = document.createElement('p');
    balanceText.innerHTML = `<strong>Current Balance:</strong> UGX ${current_balance.toLocaleString()}`;
    balanceText.style.marginBottom = '8px';
    balanceText.style.color = '#555';
    
    summary.appendChild(successText);
    summary.appendChild(costText);
    summary.appendChild(balanceText);
    
    // Recipients section
    const recipientsTitle = document.createElement('h3');
    recipientsTitle.textContent = 'Recipients';
    recipientsTitle.style.marginBottom = '15px';
    recipientsTitle.style.color = '#333';
    recipientsTitle.style.fontSize = '16px';
    
    const recipientsList = document.createElement('div');
    recipientsList.style.maxHeight = '300px';
    recipientsList.style.overflowY = 'auto';
    recipientsList.style.border = '1px solid #eee';
    recipientsList.style.borderRadius = '4px';
    recipientsList.style.padding = '10px';
    
    sent_names.forEach((name, index) => {
        const recipient = document.createElement('div');
        recipient.textContent = `${index + 1}. ${name}`;
        recipient.style.padding = '8px 5px';
        recipient.style.borderBottom = '1px solid #f5f5f5';
        recipient.style.color = '#555';
        
        // Alternate row colors
        if (index % 2 === 0) {
            recipient.style.backgroundColor = '#fafafa';
        }
        
        // Remove border from last item
        if (index === sent_names.length - 1) {
            recipient.style.borderBottom = 'none';
        }
        
        recipientsList.appendChild(recipient);
    });
    
    // Build the modal
    content.appendChild(summary);
    content.appendChild(recipientsTitle);
    content.appendChild(recipientsList);
    
    modalContent.appendChild(header);
    modalContent.appendChild(content);
    modalContainer.appendChild(modalContent);
    
    // Add to DOM
    document.body.appendChild(modalContainer);
    
    // Close modal when clicking outside content
    modalContainer.addEventListener('click', (e) => {
        if (e.target === modalContainer) {
            document.body.removeChild(modalContainer);
        }
    });
};

  const handleSearchChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    filterContacts(query);
  };

  const filterContacts = (query) => {
    if (!query) {
      setFilteredContacts(contacts);
    } else {
      const filteredContacts = contacts.filter(contact =>
        contact.first_name.toLowerCase().startsWith(query.toLowerCase())
      );
      setFilteredContacts(filteredContacts);
    }
  };

  const handleAddToWalletClick = (contactId) => {
    setSelectedContactId(contactId);
    setIsWalletPromptOpen(true);
  };
  
  const inputStyle = {
    width: "100%",
    padding: "8px",
    marginTop: "5px",
    border: "1px solid #ccc",
    borderRadius: "0px",
    fontSize: "16px",
  };
  
  const buttonStyle = {
    padding: "10px 15px",
    backgroundColor: "#007BFF",
    color: "white",
    border: "none",
    borderRadius: "0px",
    cursor: "pointer",
    fontSize: "16px",
  };

  return (
    <div>
      <ToastContainer />
      <div className="contacts-scrollable-container"
        style={{
          backgroundColor: "white",
          padding: "10px",
          borderRadius: "8px"
        }}
      >
        <Topbar token={urlToken} />
        <h1 className="page-title" style={{ marginTop: '60px' }}>ALL CLIENT DETAILS</h1>

        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            padding: "10px",
            backgroundColor: "#f8f9fa",
            borderRadius: "0px",
            boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.1)",
            width: "100%",
            maxWidth: "400px",
            margin: "20px auto",
          }}
        >
          <input
            type="text"
            placeholder="Search contact by name"
            value={searchQuery}
            onChange={handleSearchChange}
            style={{
              width: "100%",
              padding: "10px",
              fontSize: "16px",
              border: "1px solid #ccc",
              borderRadius: "5px",
              outline: "none",
              transition: "border-color 0.3s",
            }}
            onFocus={(e) => (e.target.style.borderColor = "#007bff")}
            onBlur={(e) => (e.target.style.borderColor = "#ccc")}
          />
        </div>

        {/* Recipient List Modal */}
        {showRecipientList && (
          <div className="modal-overlay">
            <div className="modal-content" style={{ maxWidth: '800px', width: '90%' }}>
              <div className="transaction-prompt">
                <h2>Recipient List for {selectedReligion} Patients</h2>
                <p>Categories: {selectedCategories.join(', ')}</p>
                <p>Total Recipients: {filteredContactsForMessaging.length}</p>
                <p style={{ color: 'green' }}>
                  <FontAwesomeIcon icon={faCheckCircle} /> Valid Numbers: {validNumbersCount}
                </p>
                <p style={{ color: 'red' }}>
                  <FontAwesomeIcon icon={faTimesCircle} /> Invalid Numbers: {invalidNumbersCount}
                </p>
                
                <div style={{ maxHeight: '400px', overflowY: 'auto', margin: '20px 0' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ backgroundColor: '#f2f2f2' }}>
                        <th style={{ padding: '10px', border: '1px solid #ddd' }}>Name</th>
                        <th style={{ padding: '10px', border: '1px solid #ddd' }}>Age</th>
                        <th style={{ padding: '10px', border: '1px solid #ddd' }}>Phone Number</th>
                        <th style={{ padding: '10px', border: '1px solid #ddd' }}>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredContactsForMessaging.map((contact, index) => (
                        <tr key={index} style={{ borderBottom: '1px solid #ddd' }}>
                          <td style={{ padding: '10px', border: '1px solid #ddd' }}>
                            {contact.first_name} {contact.last_name}
                          </td>
                          <td style={{ padding: '10px', border: '1px solid #ddd' }}>{contact.age}</td>
                          <td style={{ padding: '10px', border: '1px solid #ddd' }}>{contact.phone_number}</td>
                          <td style={{ padding: '10px', border: '1px solid #ddd', 
                              color: isValidPhoneNumber(contact.phone_number) ? 'green' : 'red' }}>
                            {isValidPhoneNumber(contact.phone_number) ? (
                              <span><FontAwesomeIcon icon={faCheckCircle} /> Valid</span>
                            ) : (
                              <span><FontAwesomeIcon icon={faTimesCircle} /> Invalid</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="transaction-buttons" style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <button 
                    onClick={() => setShowRecipientList(false)} 
                    style={{ 
                      backgroundColor: 'red', 
                      color: 'white', 
                      border: 'none', 
                      borderRadius: '0px', 
                      padding: '10px' 
                    }}
                  >
                    Cancel
                  </button>
                  <button 
                    onClick={proceedToMessageComposition} 
                    style={{ 
                      backgroundColor: 'green', 
                      color: 'white', 
                      border: 'none', 
                      borderRadius: '0px', 
                      padding: '10px', 
                      marginLeft: '10px' 
                    }}
                  >
                    Continue to Message Composition
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Rest of your modals (Category Selection, Send Message, Send Message to All, Add Contact, Edit Contact) */}
        {/* ... (keep all your existing modal code as is) ... */}

        
{/* Category Selection Modal */}
{showCategorySelectionPrompt && (
  <div className="modal-overlay">
    <div className="modal-content">
      <div className="transaction-prompt">
        <h2>Select {selectedReligion} Patient Categories</h2>
        <p>Choose which categories of {selectedReligion} patients to send messages to:</p>
        
        <div style={{ margin: '20px 0' }}>
          <label style={{ display: 'block', marginBottom: '10px' }}>
            <input
              type="checkbox"
              checked={selectedCategories.includes('active')}
              onChange={() => handleCategoryChange('active')}
              style={{ marginRight: '8px' }}
            />
            Active Patients
          </label>
          
          <label style={{ display: 'block', marginBottom: '10px' }}>
            <input
              type="checkbox"
              checked={selectedCategories.includes('inactive')}
              onChange={() => handleCategoryChange('inactive')}
              style={{ marginRight: '8px' }}
            />
            Inactive Patients
          </label>
          
          <label style={{ display: 'block', marginBottom: '10px' }}>
            <input
              type="checkbox"
              checked={selectedCategories.includes('lost')}
              onChange={() => handleCategoryChange('lost')}
              style={{ marginRight: '8px' }}
            />
            Lost Patients
          </label>
        </div>

        <div className="transaction-buttons">
          <button 
            onClick={() => {
              setShowCategorySelectionPrompt(false);
              setSelectedCategories(['active']); // Reset to default
            }} 
            style={{ 
              backgroundColor: 'red', 
              color: 'white', 
              border: 'none', 
              borderRadius: '0px', 
              padding: '10px' 
            }}
          >
            Cancel
          </button>
          <button 
            onClick={handleCategorySelection} 
            style={{ 
              backgroundColor: 'green', 
              color: 'white', 
              border: 'none', 
              borderRadius: '0px', 
              padding: '10px', 
              marginLeft: '10px' 
            }}
          >
            Continue
          </button>
        </div>
      </div>
    </div>
  </div>
)}

  {showSendMessagePrompt && (
  <div className="modal-overlay">
    <div className="modal-content">
      <div className="transaction-prompt">
        <h2>Send Message</h2>
        <textarea
  rows="4"
  cols="50"
  placeholder="Type your message here"
  value={message}
  onChange={(e) => setMessage(e.target.value)}
  style={{
    fontFamily: '"Roboto", sans-serif', // Use Roboto or any other good font
    fontSize: '14px', // Optional: you can adjust the font size as needed
    padding: '10px', // Optional: padding to make the text area look better
    borderRadius: '4px', // Optional: a little border-radius for smooth edges
    border: '1px solid #ccc' // Optional: a light border color
  }}
></textarea>


          <div className="transaction-buttons">
            {isLoading ? (
              <LoadingState />
            ) : (
              <>
              <button 
  onClick={() => setShowSendMessagePrompt(false)} 
  style={{ backgroundColor: 'red', color: 'white', border: 'none', borderRadius: '0px', padding: '10px' }}
>
  Cancel
</button>

<button 
  onClick={() => sendActualMessage(phoneNumber)} 
  style={{ backgroundColor: 'green', color: 'white', border: 'none', borderRadius: '0px', padding: '10px', marginLeft: '10px' }}
>
  Send
</button>

            </>
          )}
        </div>
      </div>
    </div>
  </div>
)}

     {showSendMessageToAllPrompt && (
  <div className="modal-overlay">
    <div className="modal-content">
      <div className="transaction-prompt">
        <h2>Send Message to {selectedReligion} Patients</h2>
        <p>Categories: {selectedCategories.join(', ')}</p>
        <p>Total Recipients: {filteredContactsForMessaging.length}</p>
        <textarea
  rows="4"
  cols="50"
  placeholder="Type your message here"
  value={selectedReligion === 'Islamic' ? messageToAllPatientsMuslims : messageToAllPatientsChristians}
  onChange={(e) =>
    selectedReligion === 'Islamic'
      ? setMessageToAllPatientsMuslims(e.target.value)
      : setMessageToAllPatientsChristians(e.target.value)
  }
  style={{
    fontFamily: '"Roboto", sans-serif', // Using Roboto font
    fontSize: '14px', // Optional: adjust the font size as needed
    padding: '10px', // Optional: adds padding for better appearance
    borderRadius: '4px', // Optional:
    border: '1px solid #ccc' // Optional: light border color
  }}
></textarea>


        <div className="transaction-buttons">
          {isLoading ? (
            <LoadingState />
          ) : (
            <>
              <button 
  onClick={() => setShowSendMessageToAllPrompt(false)} 
  style={{ 
    backgroundColor: 'red', 
    color: 'white', 
    border: 'none', 
    borderRadius: '0px', 
    padding: '10px' 
  }}
>
  Cancel
</button>

<button 
  onClick={sendToAllPatients} 
  style={{ 
    backgroundColor: 'green', 
    color: 'white', 
    border: 'none', 
    borderRadius: '0px', 
    padding: '10px', 
    marginLeft: '10px' 
  }}
>
  Send to All {newContact.religion} Patients
</button>

            </>
          )}
        </div>
      </div>
    </div>
  </div>
)}

{showAddContactPrompt && (
  <div
    className="modal-overlay"
    style={{
      position: "fixed",
      top: 0,
      left: 0,
      width: "100%",
      height: "100%",
      backgroundColor: "rgba(0, 0, 0, 0.5)",
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      zIndex: 1000,
    }}
  >
    <div
      className="modal-content"
      style={{
        backgroundColor: "#fff",
        padding: "20px",
        borderRadius: "10px",
        width: "400px",
        boxShadow: "0 4px 8px rgba(0, 0, 0, 0.2)",
      }}
    >
      <div className="transaction-prompt">
        <h2 style={{ textAlign: "center", marginBottom: "20px" }}>
          Add New Contact
        </h2>

        <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
          <label>
            First Name:
            <input
              type="text"
              value={newContact.first_name}
              onChange={(e) =>
                setNewContact({ ...newContact, first_name: e.target.value })
              }
              className="add-contact-input"
              style={inputStyle}
            />
          </label>

          <label>
            Last Name:
            <input
              type="text"
              value={newContact.last_name}
              onChange={(e) =>
                setNewContact({ ...newContact, last_name: e.target.value })
              }
              className="add-contact-input"
              style={inputStyle}
            />
          </label>

          <label>
            Phone Number:
            <input
              type="text"
              value={newContact.phone_number}
              onChange={(e) =>
                setNewContact({
                  ...newContact,
                  phone_number: e.target.value.replace(/\D/, ""),
                })
              }
              className="add-contact-input"
              style={inputStyle}
            />
          </label>

          <label>
            Address:
            <input
              type="text"
              value={newContact.address}
              onChange={(e) =>
                setNewContact({ ...newContact, address: e.target.value })
              }
              className="add-contact-input"
              style={inputStyle}
            />
          </label>

          <label>
            Age:
            <input
              type="number"
              value={newContact.age}
              onChange={(e) =>
                setNewContact({
                  ...newContact,
                  age: e.target.value.replace(/\D/, ""),
                })
              }
              className="add-contact-input"
              style={inputStyle}
            />
          </label>

          <label>
            Sex:
            <select
              value={newContact.sex}
              onChange={(e) =>
                setNewContact({ ...newContact, sex: e.target.value })
              }
              className="add-contact-input"
              style={inputStyle}
            >
              <option value="">Select Sex</option>
              <option value="Male">Male</option>
              <option value="Female">Female</option>
            </select>
          </label>

          <label>
            Date of Birth:
            <input
              type="text"
              placeholder="YYYY-MM-DD"
              value={newContact.dob}
              onChange={(e) =>
                setNewContact({ ...newContact, dob: e.target.value })
              }
              onFocus={(e) => (e.target.type = "date")}
              onBlur={(e) => (e.target.type = "text")}
              className="add-contact-input"
              style={inputStyle}
            />
          </label>

          <label>
            Religion:
            <select
              value={newContact.religion}
              onChange={(e) =>
                setNewContact({ ...newContact, religion: e.target.value })
              }
              className="add-contact-input"
              style={inputStyle}
            >
              <option value="">Select Religion</option>
              <option value="Christian">Christian</option>
              <option value="Islamic">Islamic</option>
            </select>
          </label>
        </div>

        <div
  className="transaction-buttons"
  style={{
    display: "flex",
    justifyContent: "space-between",
    marginTop: "20px",
  }}
>
  <button
    onClick={() => setShowAddContactPrompt(false)}
    style={{
      ...buttonStyle,
      backgroundColor: "red",
      color: "white",
      borderRadius: "0px", // Zero radius
    }}
  >
    Cancel
  </button>
  <button
    onClick={addContact}
    disabled={isAddingContact}
    style={{
      ...buttonStyle,
      backgroundColor: "green",
      color: "white",
      borderRadius: "0px", // Zero radius
    }}
  >
    {isAddingContact ? "Please wait..." : "Add Contact"}
  </button>
</div>

      </div>
    </div>
  </div>
)}

        <div className="custom-button-container" style={{ display: 'flex', alignItems: 'center' }}>
          <button 
            onClick={() => sendMessageToAllPatients('Islamic')} 
            disabled={!canSendReminder}
            style={{ 
              backgroundColor: 'blue', 
              color: 'white', 
              borderRadius: '0px', 
              padding: '10px 15px', 
              border: 'none', 
              cursor: canSendReminder ? 'pointer' : 'not-allowed', 
              opacity: canSendReminder ? 1 : 0.6,
              marginRight: '10px'
            }}
          >
            <FontAwesomeIcon icon={faComment} className="yellow-icon" /> SMS to Muslims
          </button>
          <button 
            onClick={() => setShowAddContactPrompt(true)} 
            style={{ 
              backgroundColor: 'green', 
              color: 'white', 
              borderRadius: '0px', 
              padding: '10px 15px', 
              border: 'none', 
              cursor: 'pointer',
              marginRight: '10px'
            }}
          >
            Add New Patient/Client
          </button>
          <button 
            onClick={() => sendMessageToAllPatients('Christian')} 
            disabled={!canSendReminder}
            style={{ 
              backgroundColor: 'blue', 
              color: 'white', 
              borderRadius: '0px', 
              padding: '10px 15px', 
              border: 'none', 
              cursor: canSendReminder ? 'pointer' : 'not-allowed', 
              opacity: canSendReminder ? 1 : 0.6
            }}
          >
            <FontAwesomeIcon icon={faComment} className="yellow-icon" /> SMS to Christians
          </button>
        </div>

        <ul className="contacts-list">
          {filteredContacts.map((contact) => (
            <li key={contact.contact_id} className="contact-item">
              <p>{contact.first_name} {contact.last_name}</p>
              <p>Phone: {contact.phone_number}</p>
              <p>Age: {contact.age}</p>
              <p>Sex: {contact.sex}</p>
              <p>Date of Birth: {contact.dob}</p>
              <p>Religion: {contact.religion}</p>
              <p>Address: {contact.address}</p>
              
              <button 
                onClick={() => editContact(contact)} 
                disabled={isAddingContact}
                style={{ 
                  backgroundColor: 'green', 
                  color: 'white', 
                  borderRadius: '0px', 
                  padding: '10px 15px', 
                  border: 'none', 
                  cursor: isAddingContact ? 'not-allowed' : 'pointer', 
                  opacity: isAddingContact ? 0.6 : 1 
                }}
              >
                <FontAwesomeIcon icon={faEdit} /> Edit Details
              </button>

              <button 
                onClick={() => sendMessage(contact.phone_number)} 
                disabled={!canSendReminder}
                style={{ 
                  backgroundColor: 'blue', 
                  color: 'white', 
                  borderRadius: '0px', 
                  padding: '10px 15px', 
                  border: 'none', 
                  cursor: canSendReminder ? 'pointer' : 'not-allowed', 
                  opacity: canSendReminder ? 1 : 0.6 
                }}
              >
                <FontAwesomeIcon icon={faComment} className="yellow-icon" /> Send SMS
              </button>
            </li>
          ))}
        </ul>

        {isWalletPromptOpen && (
          <AddToWalletPrompt
            contactId={selectedContactId}
            onClose={() => setIsWalletPromptOpen(false)}
          />
        )}

        {/* Edit Contact Modal */}
        {editContactVisible && (
          <div className="modal-overlay">
            <div className="modal-content">
              <div className="transaction-prompt">
                <h2 style={{ textAlign: "center", marginBottom: "20px" }}>
                  Edit Contact Details
                </h2>
                
                <label>First Name</label>
                <input
                  type="text"
                  value={editedContact.first_name}
                  onChange={(e) =>
                    setEditedContact({ ...editedContact, first_name: e.target.value })
                  }
                  style={{ width: "100%", padding: "8px", marginBottom: "10px" }}
                />
                
                <label>Last Name</label>
                <input
                  type="text"
                  value={editedContact.last_name}
                  onChange={(e) =>
                    setEditedContact({ ...editedContact, last_name: e.target.value })
                  }
                  style={{ width: "100%", padding: "8px", marginBottom: "10px" }}
                />
                
                <label>Phone Number</label>
                <input
                  type="text"
                  value={editedContact.phone_number}
                  onChange={(e) =>
                    setEditedContact({
                      ...editedContact,
                      phone_number: e.target.value.replace(/\D/, ""),
                    })
                  }
                  style={{ width: "100%", padding: "8px", marginBottom: "10px" }}
                />
                
                <label>Address</label>
                <input
                  type="text"
                  value={editedContact.address}
                  onChange={(e) =>
                    setEditedContact({ ...editedContact, address: e.target.value })
                  }
                  style={{ width: "100%", padding: "8px", marginBottom: "10px" }}
                />
                
                <label>Age</label>
                <input
                  type="number"
                  value={editedContact.age}
                  onChange={(e) =>
                    setEditedContact({
                      ...editedContact,
                      age: e.target.value.replace(/\D/, ""),
                    })
                  }
                  style={{ width: "100%", padding: "8px", marginBottom: "10px" }}
                />
                
                <label>Sex</label>
                <select
                  value={editedContact.sex}
                  onChange={(e) =>
                    setEditedContact({ ...editedContact, sex: e.target.value })
                  }
                  style={{ width: "100%", padding: "8px", marginBottom: "10px" }}
                >
                  <option value="">Select Sex</option>
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                </select>
                
                <label>Date of Birth (YYYY-MM-DD)</label>
                <input
                  type="text"
                  value={editedContact.dob}
                  onChange={(e) =>
                    setEditedContact({ ...editedContact, dob: e.target.value })
                  }
                  onFocus={(e) => (e.target.type = "date")}
                  onBlur={(e) => (e.target.type = "text")}
                  style={{ width: "100%", padding: "8px", marginBottom: "10px" }}
               
               />
                
                <label>Religion</label>
                <select
                  value={editedContact.religion}
                  onChange={(e) =>
                    setEditedContact({ ...editedContact, religion: e.target.value })
                  }
                  style={{ width: "100%", padding: "8px", marginBottom: "20px" }}
                >
                  <option value="">Select Religion</option>
                  <option value="Christian">Christian</option>
                  <option value="Islamic">Islamic</option>
                </select>
                
                <div style={{ display: "flex", justifyContent: "space-between" }}>
                  <button
                    onClick={() => setEditContactVisible(false)}
                    style={{
                      backgroundColor: "red",
                      border: "none",
                      padding: "10px 15px",
                      cursor: "pointer",
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={updateContact}
                    disabled={isAddingContact}
                    style={{
                      backgroundColor: "#28a745",
                      color: "white",
                      border: "none",
                      padding: "10px 15px",
                      cursor: "pointer",
                    }}
                  >
                    {isAddingContact ? "Please wait..." : "Save Changes"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default Contacts;